import * as Tone from "tone";
import { flashPianoKey as sharedFlashPianoKey, highlightPiano as sharedHighlightPiano, renderPiano as sharedRenderPiano } from "../js/music/piano.js";
import { renderGuitarDiagram } from "../js/music/guitar.js";
import { playSoundfontPreview } from "../js/music/soundfont-previews.js";

const NOTE_NAMES_SHARP = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const NOTE_NAMES_FLAT = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];
const PROGRESSION_STORAGE_KEY = "bardsCorner.circleOfFifths.progression.v1";
const SETTINGS_STORAGE_KEY = "bardsCorner.circleOfFifths.settings.v1";
const CHORD_PROGRESSIONS_STORAGE_KEY = "bardsCorner.chordProgressions.v1";
const DEFAULT_PROGRESSION_BPM = 128;
const PPQ = 960;
const MIN_BPM = 40;
const MAX_BPM = 240;
const STRUM_STEP_SECONDS = 0.018;
const BOTTOM_PAIR_GAP_ADJUSTMENT = 0.003;
const NOTE_TO_PC = {
  C: 0,
  "C#": 1,
  Db: 1,
  D: 2,
  "D#": 3,
  Eb: 3,
  E: 4,
  Fb: 4,
  "E#": 5,
  F: 5,
  "F#": 6,
  Gb: 6,
  G: 7,
  "G#": 8,
  Ab: 8,
  A: 9,
  "A#": 10,
  Bb: 10,
  B: 11,
  Cb: 11,
};
const NOTE_COLORS = ["#c73528", "#c7771f", "#a3a226", "#4fad27", "#2d92a8", "#8c22b4", "#b821b8", "#d17a19", "#57b7c5", "#88bc55", "#dc6b58", "#f0c859"];
const MAJOR_DEGREES = new Map([[0, "I"], [1, "bII"], [2, "II"], [3, "bIII"], [4, "III"], [5, "IV"], [6, "bV"], [7, "V"], [8, "bVI"], [9, "VI"], [10, "bVII"], [11, "VII"]]);
const MINOR_DEGREES = new Map([[0, "i"], [1, "bii"], [2, "ii"], [3, "iii"], [4, "biv"], [5, "iv"], [6, "bv"], [7, "v"], [8, "vi"], [9, "bVII"], [10, "vii"], [11, "bI"]]);
const ADDITIONAL_FATBOY_SOUND_IDS = [
  "accordion", "acoustic_guitar_steel", "alto_sax", "applause", "bagpipe", "banjo", "baritone_sax", "bassoon", "blown_bottle", "brass_section",
  "bright_acoustic_piano", "celesta", "cello", "choir_aahs", "church_organ", "clarinet", "contrabass", "dulcimer", "electric_bass_finger",
  "electric_bass_pick", "electric_grand_piano", "english_horn", "fiddle", "flute", "french_horn", "fretless_bass", "glockenspiel", "harmonica",
  "honkytonk_piano", "kalimba", "koto", "lead_1_square", "lead_2_sawtooth", "lead_3_calliope", "lead_4_chiff", "lead_5_charang",
  "lead_6_voice", "lead_7_fifths", "lead_8_bass__lead", "marimba", "melodic_tom", "music_box", "muted_trumpet", "oboe", "ocarina",
  "orchestral_harp", "overdriven_guitar", "pad_1_new_age", "pad_2_warm", "pad_3_polysynth", "pad_4_choir", "pad_5_bowed", "pad_6_metallic",
  "pad_7_halo", "pad_8_sweep", "pan_flute", "percussive_organ", "piccolo", "recorder", "reverse_cymbal", "shakuhachi", "shamisen",
  "shanai", "sitar", "slap_bass_1", "slap_bass_2", "soprano_sax", "steel_drums", "string_ensemble_1", "string_ensemble_2",
  "synth_bass_1", "synth_bass_2", "synth_brass_1", "synth_brass_2", "synth_choir", "synth_drum", "synth_strings_1", "synth_strings_2",
  "tango_accordion", "tenor_sax", "trombone", "trumpet", "tuba", "tubular_bells", "viola", "violin",
];
const SOUND_LABEL_WORD_OVERRIDES = { aahs: "Aahs", bass: "Bass", sax: "Sax" };
const FATBOY_SOUNDFONTS = [
  { id: "acoustic_grand_piano", label: "Acoustic Grand Piano", globalName: "acoustic_grand_piano", gain: 0.9, defaultVelocity: 0.72, release: 1.2 },
  { id: "electric_piano_1", label: "Electric Piano 1", globalName: "electric_piano_1", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "electric_guitar_clean", label: "Electric Guitar Clean", globalName: "electric_guitar_clean", gain: 0.9, defaultVelocity: 0.72, release: 1.1 },
  { id: "acoustic_guitar_nylon", label: "Acoustic Guitar Nylon", globalName: "acoustic_guitar_nylon", gain: 0.95, defaultVelocity: 0.72, release: 1.2 },
  { id: "acoustic_bass", label: "Acoustic Bass", globalName: "acoustic_bass", gain: 0.95, defaultVelocity: 0.72, release: 1.0 },
  { id: "clavinet", label: "Clavinet", globalName: "clavinet", gain: 0.9, defaultVelocity: 0.72, release: 0.7 },
  { id: "distortion_guitar", label: "Distortion Guitar", globalName: "distortion_guitar", gain: 0.82, defaultVelocity: 0.66, release: 1.2 },
  { id: "drawbar_organ", label: "Drawbar Organ", globalName: "drawbar_organ", gain: 0.75, defaultVelocity: 0.62, release: 0.8 },
  { id: "electric_guitar_jazz", label: "Electric Guitar Jazz", globalName: "electric_guitar_jazz", gain: 0.9, defaultVelocity: 0.7, release: 1.1 },
  { id: "electric_guitar_muted", label: "Electric Guitar Muted", globalName: "electric_guitar_muted", gain: 0.95, defaultVelocity: 0.72, release: 0.6 },
  { id: "electric_piano_2", label: "Electric Piano 2", globalName: "electric_piano_2", gain: 0.95, defaultVelocity: 0.68, release: 1.6 },
  { id: "harpsichord", label: "Harpsichord", globalName: "harpsichord", gain: 0.85, defaultVelocity: 0.72, release: 0.7 },
  { id: "pizzicato_strings", label: "Pizzicato Strings", globalName: "pizzicato_strings", gain: 0.9, defaultVelocity: 0.7, release: 0.8 },
  { id: "reed_organ", label: "Reed Organ", globalName: "reed_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "rock_organ", label: "Rock Organ", globalName: "rock_organ", gain: 0.78, defaultVelocity: 0.62, release: 0.8 },
  { id: "vibraphone", label: "Vibraphone", globalName: "vibraphone", gain: 0.9, defaultVelocity: 0.68, release: 1.8 },
  { id: "woodblock", label: "Woodblock", globalName: "woodblock", gain: 0.9, defaultVelocity: 0.72, release: 0.4 },
  { id: "xylophone", label: "Xylophone", globalName: "xylophone", gain: 0.9, defaultVelocity: 0.7, release: 0.7 },
  ...ADDITIONAL_FATBOY_SOUND_IDS.map((id) => ({ id, label: soundfontLabelFromId(id), globalName: id, gain: 0.9, defaultVelocity: 0.68, release: 1.2 })),
];
const CIRCLE = [
  key("C", "A", "none", [], 0),
  key("G", "E", "sharps", ["F#"], 7),
  key("D", "B", "sharps", ["F#", "C#"], 2),
  key("A", "F#", "sharps", ["F#", "C#", "G#"], 9),
  key("E", "C#", "sharps", ["F#", "C#", "G#", "D#"], 4),
  key("B", "G#", "sharps", ["F#", "C#", "G#", "D#", "A#"], 11),
  key("Gb", "Eb", "flats", ["Bb", "Eb", "Ab", "Db", "Gb", "Cb"], 6),
  key("Db", "Bb", "flats", ["Bb", "Eb", "Ab", "Db", "Gb"], 1),
  key("Ab", "F", "flats", ["Bb", "Eb", "Ab", "Db"], 8),
  key("Eb", "C", "flats", ["Bb", "Eb", "Ab"], 3),
  key("Bb", "G", "flats", ["Bb", "Eb"], 10),
  key("F", "D", "flats", ["Bb"], 5),
];
const GUITAR_SHAPES = {
  C: shape(["x", 3, 2, 0, 1, 0], 1, ["C", "E", "G", "C", "E"]),
  G: shape([3, 2, 0, 0, 0, 3], 1, ["G", "B", "D", "G", "B", "G"]),
  D: shape(["x", "x", 0, 2, 3, 2], 1, ["D", "A", "D", "F#"]),
  A: shape(["x", 0, 2, 2, 2, 0], 1, ["A", "E", "A", "C#", "E"]),
  E: shape([0, 2, 2, 1, 0, 0], 1, ["E", "B", "E", "G#", "B", "E"]),
  B: shape(["x", 2, 4, 4, 4, 2], 1, ["B", "F#", "B", "D#", "F#", "B"]),
  Gb: shape([2, 4, 4, 3, 2, 2], 1, ["Gb", "Db", "Gb", "Bb", "Db", "Gb"]),
  Db: shape(["x", 4, 6, 6, 6, 4], 1, ["Db", "Ab", "Db", "F", "Ab", "Db"]),
  Ab: shape([4, 6, 6, 5, 4, 4], 3, ["Ab", "Eb", "Ab", "C", "Eb", "Ab"]),
  Eb: shape(["x", 6, 8, 8, 8, 6], 5, ["Eb", "Bb", "Eb", "G", "Bb", "Eb"]),
  Bb: shape(["x", 1, 3, 3, 3, 1], 1, ["Bb", "F", "Bb", "D", "F", "Bb"]),
  F: shape([1, 3, 3, 2, 1, 1], 1, ["F", "C", "F", "A", "C", "F"]),
  Am: shape(["x", 0, 2, 2, 1, 0], 1, ["A", "E", "A", "C", "E"]),
  Em: shape([0, 2, 2, 0, 0, 0], 1, ["E", "B", "E", "G", "B", "E"]),
  Bm: shape(["x", 2, 4, 4, 3, 2], 1, ["B", "F#", "B", "D", "F#", "B"]),
  "F#m": shape([2, 4, 4, 2, 2, 2], 1, ["F#", "C#", "F#", "A", "C#", "F#"]),
  "C#m": shape(["x", 4, 6, 6, 5, 4], 1, ["C#", "G#", "C#", "E", "G#", "C#"]),
  "G#m": shape([4, 6, 6, 4, 4, 4], 3, ["G#", "D#", "G#", "B", "D#", "G#"]),
  Ebm: shape(["x", 6, 8, 8, 7, 6], 5, ["Eb", "Bb", "Eb", "Gb", "Bb", "Eb"]),
  Bbm: shape(["x", 1, 3, 3, 2, 1], 1, ["Bb", "F", "Bb", "Db", "F", "Bb"]),
  Fm: shape([1, 3, 3, 1, 1, 1], 1, ["F", "C", "F", "Ab", "C", "F"]),
  Cm: shape(["x", 3, 5, 5, 4, 3], 3, ["C", "G", "C", "Eb", "G", "C"]),
  Gm: shape([3, 5, 5, 3, 3, 3], 2, ["G", "D", "G", "Bb", "D", "G"]),
  Dm: shape(["x", "x", 0, 2, 3, 1], 1, ["D", "A", "D", "F"]),
};
const soundfontScriptPromises = new Map();
const audioState = {
  sampler: null,
  soundBank: "",
  ready: false,
  loadPromise: null,
};
const state = {
  selectedIndex: 0,
  selectedQuality: "major",
  topIndex: 0,
  drag: null,
  romanLocked: false,
  romanMinor: false,
  playStyle: "strum",
  soundBank: "acoustic_grand_piano",
  playbackBpm: DEFAULT_PROGRESSION_BPM,
  recording: false,
  recordedChords: [],
  selectedRecordedId: null,
  progressionPlaying: false,
  progressionTimers: [],
  playingRecordedId: null,
};

function key(major, minor, signatureType, accidentals, pitchClass) {
  return { major, minor, signatureType, accidentals, pitchClass };
}

function shape(frets, startFret, notes) {
  return { frets, startFret, notes };
}

function $(id) {
  return document.getElementById(id);
}

function makeId(prefix) {
  if (window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function soundfontLabelFromId(id) {
  return id
    .split("_")
    .filter(Boolean)
    .map((word) => SOUND_LABEL_WORD_OVERRIDES[word] || `${word[0].toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

function normalizeSoundfontId(value, fallback = "acoustic_grand_piano") {
  const raw = String(value || "").trim();
  return FATBOY_SOUNDFONTS.some((config) => config.id === raw) ? raw : fallback;
}

function soundfontConfig(id = state.soundBank) {
  return FATBOY_SOUNDFONTS.find((config) => config.id === normalizeSoundfontId(id)) || FATBOY_SOUNDFONTS[0];
}

function clampNumber(value, min, max, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
}

function mod(value, divisor) {
  return ((value % divisor) + divisor) % divisor;
}

function notePc(note) {
  return NOTE_TO_PC[note.replace(/m$/, "")];
}

function preferFlats(entry) {
  return entry.signatureType === "flats";
}

function noteNameForPc(pc, flats = false) {
  return (flats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP)[mod(pc, 12)];
}

function compactRootMidi(note) {
  const pc = notePc(note);
  return pc === 0 ? 60 : 48 + pc;
}

function displaySignature(entry) {
  if (!entry.accidentals.length) {
    return "No sharps or flats";
  }
  return entry.accidentals.join(" ");
}

function shortSignature(entry) {
  if (!entry.accidentals.length) {
    return "none";
  }
  return entry.accidentals.join(" ");
}

function selectedEntry() {
  return CIRCLE[state.selectedIndex];
}

function selectedName() {
  const entry = selectedEntry();
  return state.selectedQuality === "minor" ? `${entry.minor}m` : entry.major;
}

function chordSymbol(chord = currentChordData()) {
  return chord.quality === "minor" ? `${CIRCLE[chord.index].minor}m` : CIRCLE[chord.index].major;
}

function chordRoot(chord = currentChordData()) {
  return chord.quality === "minor" ? CIRCLE[chord.index].minor : CIRCLE[chord.index].major;
}

function chordIntervals(quality = state.selectedQuality) {
  return quality === "minor" ? [0, 3, 7] : [0, 4, 7];
}

function currentChordData() {
  return { index: state.selectedIndex, quality: state.selectedQuality };
}

function midisForChord(chord = currentChordData()) {
  const rootMidi = compactRootMidi(chordRoot(chord));
  return chordIntervals(chord.quality).map((interval) => rootMidi + interval);
}

function selectedChordMidis() {
  return midisForChord();
}

function selectedChordNotes() {
  const entry = selectedEntry();
  const root = chordRoot();
  const flats = preferFlats(entry);
  return chordIntervals().map((interval) => noteNameForPc(notePc(root) + interval, flats));
}

function soundfontScriptSrc() {
  return `/sounds/fatboy/${soundfontConfig().globalName}-ogg.js`;
}

async function ensureSoundfontData() {
  const config = soundfontConfig();
  if (window.MIDI?.Soundfont?.[config.globalName]) {
    return;
  }

  const src = soundfontScriptSrc();
  const existing = soundfontScriptPromises.get(src);
  if (existing) {
    await existing;
    return;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      if (window.MIDI?.Soundfont?.[config.globalName]) {
        resolve();
        return;
      }
      reject(new Error(`Sound bank loaded without data: ${config.label}`));
    };
    script.onerror = () => reject(new Error(`Could not load sound bank: ${config.label}`));
    document.head.append(script);
  });
  soundfontScriptPromises.set(src, loadPromise);
  await loadPromise;
}

async function ensureSampler() {
  await Tone.start();
  const config = soundfontConfig();
  if (audioState.ready && audioState.sampler && audioState.soundBank === config.id) {
    return audioState;
  }
  if (audioState.loadPromise && audioState.soundBank === config.id) {
    await audioState.loadPromise;
    return audioState;
  }

  await ensureSoundfontData();
  const soundfont = window.MIDI?.Soundfont?.[config.globalName];
  if (!soundfont) {
    throw new Error(`Sound bank is not loaded: ${config.label}`);
  }

  const output = new Tone.Gain(config.gain).toDestination();
  audioState.soundBank = config.id;
  audioState.ready = false;
  audioState.sampler = new Tone.Sampler({
    urls: { ...soundfont },
    attack: 0,
    release: config.release,
    onload() {
      audioState.ready = true;
    },
  }).connect(output);
  audioState.loadPromise = Tone.loaded().then(() => {
    audioState.ready = true;
    return audioState;
  });
  await audioState.loadPromise;
  return audioState;
}

async function playSelectedChord() {
  const midis = selectedChordMidis();
  highlightPiano(midis);
  midis.forEach(flashPianoKey);
  await playChordMidis(midis);
}

async function playChordMidis(midis) {
  try {
    const samplerState = await ensureSampler();
    const now = Tone.now();
    midis.forEach((midi, index) => {
      const config = soundfontConfig();
      samplerState.sampler?.triggerAttackRelease(
        Tone.Frequency(midi, "midi").toNote(),
        1.25,
        now + (state.playStyle === "strum" ? index * STRUM_STEP_SECONDS : 0),
        config.defaultVelocity,
      );
    });
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
  }
}

async function playChord(chord) {
  const midis = midisForChord(chord);
  highlightPiano(midis);
  midis.forEach(flashPianoKey);
  await playChordMidis(midis);
}

async function playMidi(midi, triggerElement = null) {
  try {
    const samplerState = await ensureSampler();
    samplerState.sampler?.triggerAttackRelease(
      Tone.Frequency(midi, "midi").toNote(),
      0.7,
      Tone.now(),
      soundfontConfig().defaultVelocity,
    );
    if (triggerElement) {
      triggerElement.classList.remove("is-playing");
      void triggerElement.offsetWidth;
      triggerElement.classList.add("is-playing");
      window.setTimeout(() => triggerElement.classList.remove("is-playing"), 260);
    }
  } catch (error) {
    window.alert(error.message || "Could not load that sound.");
  }
}

function renderPiano(container, onClick) {
  sharedRenderPiano(container, onClick);
}

function highlightPiano(midis) {
  sharedHighlightPiano($("piano"), midis, midis[0]);
}

function flashPianoKey(midi) {
  sharedFlashPianoKey($("piano"), midi);
}

function renderTopSelect() {
  const select = $("topKeySelect");
  select.replaceChildren(...CIRCLE.map((entry, index) => {
    const option = document.createElement("option");
    option.value = String(index);
    option.textContent = `${entry.major} at top`;
    return option;
  }));
  select.value = String(state.topIndex);
}

function renderSoundBankSelect() {
  $("soundBankSelect").replaceChildren(...FATBOY_SOUNDFONTS.map((soundfont) => {
    const option = document.createElement("option");
    option.value = soundfont.id;
    option.textContent = soundfont.label;
    return option;
  }));
}

function pointFor(index, radius, size) {
  const displayOffset = mod(index - state.topIndex, CIRCLE.length);
  const angle = -90 + displayOffset * 30;
  const radians = angle * Math.PI / 180;
  return {
    angle,
    x: Math.cos(radians) * radius * size,
    y: Math.sin(radians) * radius * size,
  };
}

function renderCircle() {
  const stage = $("circleStage");
  const rect = stage.getBoundingClientRect();
  const size = rect.width || stage.clientWidth || 640;
  const tIndexes = new Set([mod(state.selectedIndex - 1, 12), state.selectedIndex, mod(state.selectedIndex + 1, 12)]);

  stage.replaceChildren();

  CIRCLE.forEach((entry, index) => {
    stage.append(
      renderCell({ entry, index, quality: "major", radius: 0.39, size, highlighted: tIndexes.has(index) }),
      renderCell({ entry, index, quality: "minor", radius: 0.265, size, highlighted: index === state.selectedIndex }),
      renderDegreeLabel({ index, radius: 0.525, size, quality: "major" }),
      renderDegreeLabel({ index, radius: 0.16, size, quality: "minor" }),
    );
  });
}

function degreeForIndex(index, quality) {
  const sourceRoot = romanSourceRoot();
  const targetRoot = quality === "minor" ? CIRCLE[index].minor : CIRCLE[index].major;
  const offset = mod(notePc(targetRoot) - notePc(sourceRoot), 12);
  const degrees = romanSourceQuality() === "minor" ? MINOR_DEGREES : MAJOR_DEGREES;
  const degree = degrees.get(offset) || "";
  return quality === "major" ? uppercaseRomanDegree(degree) : degree.toLowerCase();
}

function uppercaseRomanDegree(degree) {
  return degree.replace(/[ivx]+/gi, (roman) => roman.toUpperCase());
}

function romanSourceQuality() {
  if (state.romanLocked) {
    return state.romanMinor ? "minor" : "major";
  }
  return state.selectedQuality;
}

function romanSourceRoot() {
  if (state.romanLocked) {
    return state.romanMinor ? CIRCLE[state.topIndex].minor : CIRCLE[state.topIndex].major;
  }
  return chordRoot();
}

function renderDegreeLabel({ index, radius, size, quality }) {
  const point = pointFor(index, radius, size);
  const label = document.createElement("span");
  label.className = `degree-label ${quality}-degree`;
  label.classList.toggle("is-tonic", notePc(quality === "minor" ? CIRCLE[index].minor : CIRCLE[index].major) === notePc(romanSourceRoot()));
  label.style.setProperty("--x", `${point.x}px`);
  label.style.setProperty("--y", `${point.y}px`);
  label.textContent = degreeForIndex(index, quality);
  return label;
}

function renderCell({ entry, index, quality, radius, size, highlighted }) {
  const gapAdjustment = mod(index - state.topIndex, CIRCLE.length) === 6 ? BOTTOM_PAIR_GAP_ADJUSTMENT : 0;
  const adjustedRadius = quality === "major" ? radius + gapAdjustment : radius - gapAdjustment;
  const point = pointFor(index, adjustedRadius, size);
  const button = document.createElement("button");
  const isSelected = index === state.selectedIndex && quality === state.selectedQuality;
  const label = quality === "minor" ? `${entry.minor}m` : entry.major;
  button.type = "button";
  button.className = `circle-cell ${quality}-cell`;
  button.classList.toggle("is-selected", isSelected);
  button.classList.toggle("is-t-highlight", highlighted);
  button.style.setProperty("--x", `${point.x}px`);
  button.style.setProperty("--y", `${point.y}px`);
  button.style.setProperty("--cell-rotation", `${point.angle - 90}deg`);
  button.style.setProperty("--label-rotation", `${90 - point.angle}deg`);
  button.style.setProperty("--cell-color", NOTE_COLORS[index]);
  button.dataset.index = String(index);
  button.dataset.quality = quality;
  button.setAttribute("aria-label", `${label}, ${displaySignature(entry)}`);
  button.innerHTML = `
    <span>
      <span class="cell-main">${label}</span>
      <span class="cell-sub">${quality === "minor" ? "minor" : "major"}</span>
      <span class="cell-sig">${shortSignature(entry)}</span>
    </span>
  `;
  button.addEventListener("pointerdown", (event) => event.stopPropagation());
  button.addEventListener("click", () => selectCell(index, quality));
  return button;
}

function selectCell(index, quality, options = {}) {
  const shouldPlay = options.play !== false;
  state.selectedIndex = index;
  state.selectedQuality = quality;
  if (state.recording && options.record !== false) {
    recordCurrentChord();
  }
  renderAll();
  if (shouldPlay) {
    playSelectedChord();
  }
}

function spin(delta) {
  state.topIndex = mod(state.topIndex + delta, CIRCLE.length);
  $("topKeySelect").value = String(state.topIndex);
  renderCircle();
}

function renderSelection() {
  const entry = selectedEntry();
  const left = CIRCLE[mod(state.selectedIndex - 1, 12)];
  const right = CIRCLE[mod(state.selectedIndex + 1, 12)];
  const selectedLabel = selectedName();
  const chordNotes = selectedChordNotes();
  $("selectedQuality").textContent = state.selectedQuality === "minor" ? "Minor" : "Major";
  $("selectedName").textContent = selectedLabel;
  $("neighborFacts").textContent = `${left.major} · ${entry.major} · ${right.major}`;
  $("relativeFacts").textContent = `${entry.minor} minor`;
  $("signatureFacts").textContent = displaySignature(entry);
  $("chordTypeLabel").textContent = state.selectedQuality === "minor" ? "Minor chord" : "Major chord";
  $("chordTitle").textContent = `${selectedLabel.replace(/m$/, "")} ${state.selectedQuality}`;
  $("chordNotes").textContent = chordNotes.join(" · ");
}

function renderGuitar() {
  const name = selectedName();
  const fallbackName = state.selectedQuality === "minor" ? "Am" : "C";
  const currentShape = GUITAR_SHAPES[name] || GUITAR_SHAPES[fallbackName];
  renderGuitarDiagram($("guitarDiagram"), currentShape, {
    root: name,
    flats: preferFlats(selectedEntry()),
    onNoteClick: ({ midi, element }) => playMidi(midi, element),
  });
}

function readStoredProgression() {
  try {
    const raw = window.localStorage.getItem(PROGRESSION_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed?.chords) ? parsed.chords : [];
  } catch {
    return [];
  }
}

function readStoredSettings() {
  try {
    const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSettings() {
  try {
    window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({
      version: 1,
      romanLocked: state.romanLocked,
      romanMinor: state.romanMinor,
      playStyle: state.playStyle,
      soundBank: state.soundBank,
      playbackBpm: state.playbackBpm,
    }));
  } catch {
    // Settings are convenience state; the tool remains usable without storage.
  }
}

function loadSettings() {
  const settings = readStoredSettings() || {};
  state.romanLocked = Boolean(settings.romanLocked);
  state.romanMinor = Boolean(settings.romanMinor);
  state.playStyle = settings.playStyle === "block" ? "block" : "strum";
  state.soundBank = normalizeSoundfontId(settings.soundBank);
  state.playbackBpm = Math.trunc(clampNumber(settings.playbackBpm, MIN_BPM, MAX_BPM, DEFAULT_PROGRESSION_BPM));
}

function renderSettingsControls() {
  $("lockRomanInput").checked = state.romanLocked;
  $("minorRomanInput").checked = state.romanMinor;
  $("minorRomanInput").disabled = !state.romanLocked;
  $("minorRomanInput").closest(".option-check")?.classList.toggle("is-disabled", !state.romanLocked);
  $("playStyleSelect").value = state.playStyle;
  $("soundBankSelect").value = state.soundBank;
  $("bpmInput").value = String(state.playbackBpm);
}

function saveProgression() {
  try {
    window.localStorage.setItem(PROGRESSION_STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: new Date().toISOString(),
      chords: state.recordedChords,
    }));
  } catch {
    // Local progression capture should keep working even if storage is unavailable.
  }
}

function normalizeStoredChord(chord) {
  const index = Math.trunc(Number(chord?.index));
  const quality = chord?.quality === "minor" ? "minor" : "major";
  if (!Number.isInteger(index) || index < 0 || index >= CIRCLE.length) {
    return null;
  }
  return {
    id: String(chord.id || makeId("rec")),
    index,
    quality,
  };
}

function loadProgression() {
  state.recordedChords = readStoredProgression().map(normalizeStoredChord).filter(Boolean);
}

function recordCurrentChord() {
  const chord = {
    id: makeId("rec"),
    index: state.selectedIndex,
    quality: state.selectedQuality,
  };
  state.recordedChords.push(chord);
  state.selectedRecordedId = chord.id;
  saveProgression();
}

function renderRecorder() {
  const recordButton = $("recordButton");
  const playButton = $("progressionPlayButton");
  const deleteButton = $("deleteChordButton");
  const clearButton = $("clearProgressionButton");
  const createSongButton = $("createSongButton");
  const list = $("recordedChordList");
  const hasChords = state.recordedChords.length > 0;
  recordButton.classList.toggle("is-recording", state.recording);
  recordButton.setAttribute("aria-pressed", String(state.recording));
  playButton.classList.toggle("active", state.progressionPlaying);
  playButton.disabled = !hasChords;
  deleteButton.disabled = !hasChords || !state.selectedRecordedId;
  clearButton.disabled = !hasChords;
  createSongButton.disabled = !hasChords;

  if (!hasChords) {
    const empty = document.createElement("p");
    empty.className = "recorded-empty";
    empty.textContent = "No chords recorded yet.";
    list.replaceChildren(empty);
    return;
  }

  list.replaceChildren(...state.recordedChords.map((chord, index) => {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "recorded-chord";
    button.classList.toggle("is-selected", chord.id === state.selectedRecordedId);
    button.classList.toggle("is-playing", chord.id === state.playingRecordedId);
    button.innerHTML = `<strong>${chordSymbol(chord)}</strong><span>${index + 1}</span>`;
    button.addEventListener("click", () => selectRecordedChord(chord.id));
    return button;
  }));
}

function selectRecordedChord(id, options = {}) {
  const chord = state.recordedChords.find((item) => item.id === id);
  if (!chord) {
    return;
  }
  state.selectedRecordedId = id;
  state.selectedIndex = chord.index;
  state.selectedQuality = chord.quality;
  renderAll();
  if (options.play !== false) {
    playChord(chord);
  }
}

function deleteSelectedChord() {
  if (!state.selectedRecordedId) {
    return;
  }
  const wasPlayingDeletedChord = state.playingRecordedId === state.selectedRecordedId;
  state.recordedChords = state.recordedChords.filter((chord) => chord.id !== state.selectedRecordedId);
  state.selectedRecordedId = null;
  if (wasPlayingDeletedChord) {
    state.playingRecordedId = null;
  }
  if (!state.recordedChords.length) {
    stopProgressionPlayback();
  }
  saveProgression();
  renderRecorder();
}

function clearProgression() {
  stopProgressionPlayback();
  state.recordedChords = [];
  state.selectedRecordedId = null;
  saveProgression();
  renderRecorder();
}

function stopProgressionPlayback() {
  state.progressionTimers.forEach((timer) => window.clearTimeout(timer));
  state.progressionTimers = [];
  state.progressionPlaying = false;
  state.playingRecordedId = null;
  renderRecorder();
}

function toggleProgressionPlayback() {
  if (state.progressionPlaying) {
    stopProgressionPlayback();
    return;
  }
  if (!state.recordedChords.length) {
    return;
  }
  state.progressionPlaying = true;
  scheduleProgressionChord(0);
  renderRecorder();
}

function scheduleProgressionChord(index) {
  if (!state.progressionPlaying || !state.recordedChords.length) {
    stopProgressionPlayback();
    return;
  }
  const chord = state.recordedChords[index % state.recordedChords.length];
  state.playingRecordedId = chord.id;
  selectRecordedChord(chord.id, { play: false });
  playChord(chord);
  renderRecorder();
  const chordMs = (60 / state.playbackBpm) * 4 * 1000;
  const timer = window.setTimeout(() => scheduleProgressionChord(index + 1), chordMs);
  state.progressionTimers.push(timer);
}

function createSongFromProgression() {
  if (!state.recordedChords.length) {
    return;
  }
  stopProgressionPlayback();
  const now = new Date().toISOString();
  const songId = makeId("song");
  const sectionId = makeId("sec");
  const ticksPerBar = PPQ * 4;
  const sectionDuration = state.recordedChords.length * ticksPerBar;
  const songKey = songKeySignature();
  const firstChordEventId = makeId("chord");
  const chordEvents = state.recordedChords.map((chord, index) => (
    chordEventFromRecorded(chord, index * ticksPerBar, ticksPerBar, index === 0 ? firstChordEventId : null)
  ));
  const song = {
    schema: "ChordSongJSON",
    schemaVersion: "0.1.0",
    id: songId,
    title: "Circle of Fifths Progression",
    createdAt: now,
    updatedAt: now,
    ppq: PPQ,
    metadata: { composer: "", lyricist: "", notes: "Created from Circle of Fifths step record." },
    timeline: {
      defaultTempoBpm: state.playbackBpm,
      tempoEvents: [{ tick: 0, bpm: state.playbackBpm }],
      timeSignatureEvents: [{ tick: 0, numerator: 4, denominator: 4 }],
      keySignatureEvents: [{ tick: 0, key: songKey.key, mode: songKey.mode }],
    },
    form: {
      sections: [{ id: sectionId, name: "Verse 1", startTick: 0, durationTicks: sectionDuration }],
      playOrder: [{ sectionId }],
    },
    tracks: [
      makeSongTrack("track_chords", "Chords", "chords", state.soundBank, chordEvents),
      makeSongTrack("track_melody", "Melody", "melody", "acoustic_grand_piano"),
      makeSongTrack("track_bass", "Bass", "bass", "acoustic_bass"),
      makeSongTrack("track_drums", "Drums", "drums", "drums", [], 10),
      makeSongTrack("track_lyrics", "Lyrics", "lyrics", "voice"),
    ],
    markers: [{ id: makeId("marker"), tick: 0, name: "Verse 1", markerType: "section" }],
  };
  try {
    window.localStorage.setItem(CHORD_PROGRESSIONS_STORAGE_KEY, JSON.stringify({
      version: 1,
      savedAt: now,
      song,
      settings: {
        snapTicks: ticksPerBar,
        loop: true,
        loopRegion: { startTick: 0, endTick: sectionDuration },
        selectedProgramValue: state.soundBank,
        playheadTick: 0,
        selectedEventId: firstChordEventId,
        lastSelectedChordId: firstChordEventId,
        collapsedTrackIds: [],
        defaultChordPlayStyle: state.playStyle,
        chordRenderMode: "simple",
        serverSongId: null,
        serverSongSignature: null,
      },
    }));
  } catch {
    return;
  }
  window.location.href = "/songs/";
}

function songKeySignature() {
  const topEntry = CIRCLE[state.topIndex];
  if (state.romanMinor) {
    return { key: topEntry.minor, mode: "minor" };
  }
  return { key: topEntry.major, mode: "major" };
}

function makeSongTrack(id, name, type, instrumentName, events = [], midiChannel = null) {
  return {
    id,
    name,
    type,
    instrumentName,
    ...(midiChannel ? { midiChannel } : {}),
    muted: false,
    solo: false,
    volume: 1,
    pan: 0,
    octaveShift: 0,
    source: { originalTempoBpm: state.playbackBpm, originalPpq: PPQ, importedFrom: "circle-of-fifths" },
    events,
  };
}

function chordEventFromRecorded(chord, tick, durationTicks, eventId = null) {
  return {
    id: eventId || makeId("chord"),
    eventType: "chord",
    tick,
    durationTicks,
    symbol: chordSymbol(chord),
    root: chordRoot(chord),
    quality: chord.quality,
    extensions: [],
    voicing: midisForChord(chord),
    playStyle: state.playStyle,
    guitarVoicingIndex: 0,
  };
}

function renderAll() {
  renderCircle();
  renderSelection();
  renderGuitar();
  highlightPiano(selectedChordMidis());
  renderRecorder();
}

function angleFromEvent(event) {
  const rect = $("circleStage").getBoundingClientRect();
  const x = event.clientX - rect.left - rect.width / 2;
  const y = event.clientY - rect.top - rect.height / 2;
  return Math.atan2(y, x) * 180 / Math.PI;
}

function initDragSpin() {
  const stage = $("circleStage");
  stage.addEventListener("pointerdown", (event) => {
    if (event.target.closest(".circle-cell")) {
      return;
    }
    state.drag = {
      pointerId: event.pointerId,
      startAngle: angleFromEvent(event),
      startTopIndex: state.topIndex,
    };
    stage.setPointerCapture(event.pointerId);
  });
  stage.addEventListener("pointermove", (event) => {
    if (!state.drag || state.drag.pointerId !== event.pointerId) {
      return;
    }
    const angleDelta = angleFromEvent(event) - state.drag.startAngle;
    const steps = Math.round(angleDelta / 30);
    const nextTop = mod(state.drag.startTopIndex - steps, CIRCLE.length);
    if (nextTop !== state.topIndex) {
      state.topIndex = nextTop;
      $("topKeySelect").value = String(state.topIndex);
      renderCircle();
    }
  });
  stage.addEventListener("pointerup", () => {
    state.drag = null;
  });
  stage.addEventListener("pointercancel", () => {
    state.drag = null;
  });
}

function isTypingTarget(element) {
  const tagName = element?.tagName;
  return ["INPUT", "TEXTAREA", "SELECT"].includes(tagName) || Boolean(element?.isContentEditable);
}

function handleKeyDown(event) {
  if (isTypingTarget(document.activeElement) || event.ctrlKey || event.metaKey || event.altKey) {
    return;
  }
  if (event.code === "Space") {
    event.preventDefault();
    toggleProgressionPlayback();
    return;
  }
  if ((event.key === "Delete" || event.key === "Backspace") && state.selectedRecordedId) {
    event.preventDefault();
    deleteSelectedChord();
  }
}

function init() {
  loadSettings();
  loadProgression();
  renderTopSelect();
  renderSoundBankSelect();
  renderSettingsControls();
  renderPiano($("piano"), (midi) => {
    ensureSampler().then((samplerState) => {
      samplerState.sampler?.triggerAttackRelease(
        Tone.Frequency(midi, "midi").toNote(),
        0.55,
        Tone.now(),
        soundfontConfig().defaultVelocity,
      );
      flashPianoKey(midi);
    }).catch(() => {});
  });
  $("spinLeftButton").addEventListener("click", () => spin(-1));
  $("spinRightButton").addEventListener("click", () => spin(1));
  $("topKeySelect").addEventListener("change", (event) => {
    state.topIndex = Number(event.target.value) || 0;
    renderCircle();
  });
  $("lockRomanInput").addEventListener("change", (event) => {
    state.romanLocked = event.target.checked;
    if (!state.romanLocked) {
      state.romanMinor = false;
    }
    saveSettings();
    renderSettingsControls();
    renderCircle();
  });
  $("minorRomanInput").addEventListener("change", (event) => {
    state.romanMinor = event.target.checked;
    saveSettings();
    renderCircle();
  });
  $("playStyleSelect").addEventListener("change", (event) => {
    state.playStyle = event.target.value === "block" ? "block" : "strum";
    saveSettings();
  });
  $("soundBankSelect").addEventListener("change", (event) => {
    state.soundBank = normalizeSoundfontId(event.target.value);
    playSoundfontPreview("fatboy", state.soundBank);
    saveSettings();
  });
  $("bpmInput").addEventListener("change", (event) => {
    state.playbackBpm = Math.trunc(clampNumber(event.target.value, MIN_BPM, MAX_BPM, DEFAULT_PROGRESSION_BPM));
    saveSettings();
    renderSettingsControls();
  });
  $("replayButton").addEventListener("click", () => playSelectedChord());
  $("recordButton").addEventListener("click", () => {
    state.recording = !state.recording;
    renderRecorder();
  });
  $("progressionPlayButton").addEventListener("click", () => toggleProgressionPlayback());
  $("deleteChordButton").addEventListener("click", () => deleteSelectedChord());
  $("clearProgressionButton").addEventListener("click", () => clearProgression());
  $("createSongButton").addEventListener("click", () => createSongFromProgression());
  window.addEventListener("keydown", handleKeyDown);
  window.addEventListener("resize", () => renderCircle());
  initDragSpin();
  renderAll();
}

init();
